<template>
  <div
    class="bg-gray-50 p-5 rounded-2xl border border-gray-100 flex flex-col gap-4"
  >
    <!-- Order Details Section - Cart Summary Style -->
    <div v-if="cartItems && cartItems.length > 0" class="pt-2">
      <!-- Collapsible Header -->
      <button
        @click="isOrderSummaryExpanded = !isOrderSummaryExpanded"
        class="flex items-center justify-between w-full mb-3 group hover:opacity-80 transition-opacity"
      >
        <h4 class="text-sm font-bold text-gray-500 uppercase tracking-wider">
          Order Summary
        </h4>
        <ChevronDown
          :size="18"
          class="text-gray-400 transition-transform duration-200"
          :class="{ 'rotate-180': isOrderSummaryExpanded }"
        />
      </button>

      <!-- Items List (Collapsible) -->
      <transition name="slide-fade">
        <div v-show="isOrderSummaryExpanded" class="space-y-3 mb-4">
          <div v-for="item in cartItems" :key="item.cartId" class="space-y-2">
            <div class="flex items-center gap-3">
              <!-- Image with Count -->
              <div class="flex items-center gap-3 flex-shrink-0">
                <div
                  class="w-12 h-12 rounded-lg overflow-hidden border border-gray-200 bg-gray-100"
                >
                  <img
                    v-if="getItemImage(item)"
                    :src="getItemImage(item)"
                    class="w-full h-full object-contain"
                    :alt="item.name"
                    @error="handleImageError"
                  />
                  <div
                    v-else
                    class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200"
                  >
                    <FileImage :size="16" class="text-gray-400" />
                  </div>
                </div>
                <span
                  class="min-w-[2rem] px-2.5 py-1 rounded-full bg-primary-600 text-white text-sm font-bold text-center shadow-sm whitespace-nowrap"
                >
                  x{{ item.quantity }}
                </span>
              </div>

              <!-- Title, Modifiers, Notes -->
              <div class="flex-1 min-w-0 flex flex-col">
                <!-- Title -->
                <span class="font-semibold text-gray-900 text-base">
                  {{ item.name }}
                </span>

                <!-- Modifiers -->
                <div
                  v-if="
                    item.selectedModifiers && item.selectedModifiers.length > 0
                  "
                  class="space-y-0.5"
                >
                  <div
                    v-for="(modifier, index) in item.selectedModifiers"
                    :key="index"
                    class="text-sm text-gray-500 leading-relaxed"
                  >
                    {{ modifier.name }}
                  </div>
                </div>

                <!-- Notes -->
                <div v-if="item.note">
                  <p class="text-sm text-gray-500 italic">
                    <span class="font-semibold text-gray-600 not-italic"
                      >Note:</span
                    >
                    "{{ item.note }}"
                  </p>
                </div>
              </div>

              <!-- Line Price -->
              <div class="flex-shrink-0 flex flex-col items-end gap-1">
                <div
                  v-if="item.quantity > 1"
                  class="flex items-baseline justify-end gap-2 text-sm whitespace-nowrap"
                >
                  <span
                    v-if="hasDiscount(item)"
                    class="text-gray-400 line-through"
                  >
                    {{ formatPrice(getUnitPrice(item)) }}
                  </span>
                  <span
                    :class="
                      hasDiscount(item)
                        ? 'text-green-700 font-semibold'
                        : 'text-gray-400'
                    "
                  >
                    {{ formatPrice(getDiscountedUnitPrice(item)) }} each
                  </span>
                </div>
                <div
                  class="flex items-baseline justify-end gap-2.5 whitespace-nowrap"
                >
                  <span
                    v-if="hasDiscount(item)"
                    class="text-base text-gray-400 line-through"
                  >
                    {{ formatPrice(getLineOriginal(item)) }}
                  </span>
                  <span
                    class="font-bold text-lg"
                    :class="
                      hasDiscount(item) ? 'text-green-700' : 'text-primary-600'
                    "
                  >
                    {{ formatPrice(getLineDiscounted(item)) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Promotion labels (full-width row under the product) -->
            <div
              v-if="getPromotionLabels(item).length"
              class="flex flex-wrap gap-1"
            >
              <span
                v-for="label in getPromotionLabels(item)"
                :key="label"
                class="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 rounded-full px-2 py-0.5"
              >
                <Tag :size="11" />
                {{ label }}
              </span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { FileImage, ChevronDown, Tag } from "lucide-vue";
export default {
  name: "OrderSummary",
  components: {
    ChevronDown,
    FileImage,
    Tag
  },
  props: {
    cartItems: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isOrderSummaryExpanded: true
    };
  },
  methods: {
    getItemImage(item) {
      return item?.image || item?.image_url || null;
    },
    getUnitPrice(item) {
      const modifiersPrice =
        item.selectedModifiers?.reduce((sum, m) => sum + (m.price || 0), 0) ||
        0;
      return Number(item.price || 0) + modifiersPrice;
    },
    getLineOriginal(item) {
      if (typeof item.itemTotalWithoutDiscount === "number") {
        return item.itemTotalWithoutDiscount;
      }
      return this.getUnitPrice(item) * Number(item.quantity || 0);
    },
    getLineDiscounted(item) {
      if (typeof item.itemTotal === "number") {
        return item.itemTotal;
      }
      return this.getLineOriginal(item);
    },
    getDiscountedUnitPrice(item) {
      const quantity = Number(item.quantity || 0);
      if (quantity <= 0) return this.getUnitPrice(item);
      return this.getLineDiscounted(item) / quantity;
    },
    hasDiscount(item) {
      return this.getLineOriginal(item) - this.getLineDiscounted(item) > 0.005;
    },
    getPromotionLabels(item) {
      if (!Array.isArray(item.promotionLabels)) return [];
      return [...new Set(item.promotionLabels.filter(Boolean))];
    },
    handleImageError(event) {
      event.target.style.display = "none";
    }
  },
  computed: {
    ...mapGetters(["getCurrencySymbol", "formatPrice"]),
    currency() {
      return this.getCurrencySymbol || "$";
    }
  }
};
</script>

<style></style>
